import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { PiX } from 'react-icons/pi';
import './styles.css';

const CLOSE_DELAY_MS = 180;

let openOverlayCount = 0;
let lockedScrollY = 0;
let lockedLayoutWidth = 0;
let previousBodyStyles = null;
let previousAppRootState = null;

const BottomSheet = ({
  title,
  ariaLabel,
  subtitle,
  children,
  footer,
  headerActions,
  onClose,
  size = 'md',
  showHandle = true,
  closeOnBackdrop = true,
  className = '',
}) => {
  const titleId = `nf-sheet-title-${useId().replace(/:/g, '')}`;
  const panelRef = useRef(null);
  const closeTimerRef = useRef(null);
  const previousFocusRef = useRef(null);
  const [closing, setClosing] = useState(false);

  const requestClose = useCallback(() => {
    if (closing) return;

    setClosing(true);
    closeTimerRef.current = window.setTimeout(onClose, CLOSE_DELAY_MS);
  }, [closing, onClose]);

  useEffect(() => {
    previousFocusRef.current = document.activeElement;

    openOverlayCount += 1;

    const isDesktop = window.matchMedia('(min-width: 768px)').matches;

    if (openOverlayCount === 1) {
      lockedScrollY = window.scrollY;
      lockedLayoutWidth = document.documentElement.clientWidth || window.innerWidth;

      previousBodyStyles = {
        position: document.body.style.position,
        top: document.body.style.top,
        left: document.body.style.left,
        right: document.body.style.right,
        width: document.body.style.width,
        maxWidth: document.body.style.maxWidth,
        overflow: document.body.style.overflow,
      };

      const appRoot = document.getElementById('root');
      previousAppRootState = appRoot ? {
        inert: Boolean(appRoot.inert),
        ariaHidden: appRoot.getAttribute('aria-hidden'),
      } : null;
      if (appRoot) {
        appRoot.inert = true;
        appRoot.setAttribute('aria-hidden', 'true');
      }

      document.body.classList.add('nf-overlay-open');
      document.body.style.width = `${lockedLayoutWidth}px`;
      document.body.style.maxWidth = '100%';

      if (isDesktop) {
        document.body.style.position = 'fixed';
        document.body.style.top = `-${lockedScrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = 'auto';
      }

      document.body.style.overflow = 'hidden';
    }

    window.requestAnimationFrame(() => {
      panelRef.current?.focus({
        preventScroll: true,
      });
    });

    return () => {
      window.clearTimeout(closeTimerRef.current);

      openOverlayCount = Math.max(0, openOverlayCount - 1);

      if (openOverlayCount === 0) {
        document.body.classList.remove('nf-overlay-open');

        document.body.style.position = previousBodyStyles.position;
        document.body.style.top = previousBodyStyles.top;
        document.body.style.left = previousBodyStyles.left;
        document.body.style.right = previousBodyStyles.right;
        document.body.style.width = previousBodyStyles.width;
        document.body.style.maxWidth = previousBodyStyles.maxWidth;
        document.body.style.overflow = previousBodyStyles.overflow;

        const appRoot = document.getElementById('root');
        if (appRoot && previousAppRootState) {
          appRoot.inert = previousAppRootState.inert;
          if (previousAppRootState.ariaHidden === null) appRoot.removeAttribute('aria-hidden');
          else appRoot.setAttribute('aria-hidden', previousAppRootState.ariaHidden);
        }
        previousAppRootState = null;

        if (isDesktop) {
          window.scrollTo({
            top: lockedScrollY,
            left: 0,
            behavior: 'instant',
          });
        }
      }

      previousFocusRef.current?.focus?.({
        preventScroll: true,
      });
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        requestClose();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [href], [tabindex]:not([tabindex="-1"])'
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [requestClose]);

  return createPortal(
    <div
      className={`nf-sheet-backdrop ${closing ? 'nf-sheet-backdrop--closing' : ''} ${className}`.trim()}
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) {
          requestClose();
        }
      }}
    >
      <section
        ref={panelRef}
        className={`nf-sheet nf-sheet--${size} ${closing ? 'nf-sheet--closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel ? undefined : titleId}
        tabIndex={-1}
      >
        {showHandle && <span className="nf-sheet__handle" aria-hidden="true" />}

        <header className="nf-sheet__header">
          <div className="nf-sheet__heading">
            <h2 id={titleId}>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>

          <div className="nf-sheet__header-actions">
            {headerActions}
            <button
              type="button"
              className="nf-sheet__close"
              onClick={requestClose}
              aria-label={`Fechar ${title}`}
            >
              <PiX size={20} aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="nf-sheet__body">{children}</div>

        {footer && <footer className="nf-sheet__footer">{footer}</footer>}
      </section>
    </div>,
    document.body
  );
};

BottomSheet.propTypes = {
  title: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  headerActions: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'calculator']),
  showHandle: PropTypes.bool,
  closeOnBackdrop: PropTypes.bool,
  className: PropTypes.string,
};

export { CLOSE_DELAY_MS };
export default BottomSheet;

